import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Skeleton,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  IconButton,
  alpha,
  Divider,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Zap,
  Clock,
  BarChart3,
  ArrowRight,
  CheckCircle,
  XCircle,
  Activity,
} from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus } from '@/types';

// Period type for performance tracking
type PerformancePeriod = 'day' | 'week' | 'month' | 'year';

interface PerformanceData {
  completed: number;
  missed: number;
  total: number;
  hitRate: number;
  estimatedHours: number;
  actualHours: number;
  velocity: number; // actual/estimated
  tasks: Task[];
}

/**
 * DailyPerformanceWidget
 * Shows comprehensive performance tracking with hit/miss metrics and velocity
 * Tracks: Daily, Weekly, Monthly, Yearly performance with time-based velocity
 */
const DailyPerformanceWidget: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [period, setPeriod] = useState<PerformancePeriod>('day');

  // Fetch today's tasks
  const { data: todaysTasksData, isLoading } = useQuery({
    queryKey: QUERY_KEYS.todaysTasks,
    queryFn: () => taskService.getTodaysTasks(),
    staleTime: 60000,
  });

  // Fetch all tasks for historical analysis
  const { data: allTasksData, isLoading: isLoadingAll } = useQuery({
    queryKey: QUERY_KEYS.tasks,
    queryFn: () => taskService.getTasks(),
    staleTime: 60000,
  });

  const todaysTasks = todaysTasksData ?? [];
  const allTasks = allTasksData?.items ?? [];

  // Calculate performance for selected period
  const calculatePerformance = (periodType: PerformancePeriod): PerformanceData => {
    const now = dayjs();
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs = now;

    switch (periodType) {
      case 'day':
        startDate = now.startOf('day');
        break;
      case 'week':
        startDate = now.startOf('week');
        break;
      case 'month':
        startDate = now.startOf('month');
        break;
      case 'year':
        startDate = now.startOf('year');
        break;
      default:
        startDate = now.startOf('day');
    }

    // Filter tasks in the period
    const periodTasks = periodType === 'day' 
      ? todaysTasks 
      : allTasks.filter(task => {
          const dueDate = dayjs(task.dueDate);
          return dueDate.isAfter(startDate) && dueDate.isBefore(endDate);
        });

    const completed = periodTasks.filter(t => t.status === TaskStatus.COMPLETED).length;
    
    // Calculate missed tasks:
    // 1. Tasks that are overdue (past due date and not completed/cancelled)
    // 2. Tasks that were rescheduled from this period (they count as misses)
    const todayStr = now.format('YYYY-MM-DD');
    const missed = periodTasks.filter(t => {
      // Skip completed or cancelled tasks
      if (t.status === TaskStatus.COMPLETED || t.status === TaskStatus.CANCELLED) {
        return false;
      }
      
      // Check if task was rescheduled from today (counts as a miss)
      const wasRescheduledFromToday = t.rescheduledFromDates?.includes(todayStr) || false;
      if (wasRescheduledFromToday) {
        return true;
      }
      
      // Check if task is overdue (past due date)
      return dayjs(t.dueDate).isBefore(now);
    }).length;
    
    const total = periodTasks.length;
    const hitRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Calculate time-based velocity
    const estimatedHours = periodTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
    const actualHours = periodTasks
      .filter(t => t.status === TaskStatus.COMPLETED)
      .reduce((sum, t) => sum + (t.actualHours || t.estimatedHours || 0), 0);
    
    // Velocity: actual/estimated (< 1 means faster, > 1 means slower)
    const velocity = estimatedHours > 0 ? actualHours / estimatedHours : 0;

    return {
      completed,
      missed,
      total,
      hitRate,
      estimatedHours,
      actualHours,
      velocity,
      tasks: periodTasks,
    };
  };

  const performance = calculatePerformance(period);

  // Determine status color
  const getStatusColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  // Determine velocity status (good if velocity < 1, meaning faster than estimated)
  const getVelocityStatus = (velocity: number) => {
    if (velocity === 0) return { color: theme.palette.text.secondary, label: 'No data', icon: Activity };
    if (velocity <= 0.8) return { color: theme.palette.success.main, label: 'Excellent', icon: TrendingUp };
    if (velocity <= 1.2) return { color: theme.palette.warning.main, label: 'On track', icon: Activity };
    return { color: theme.palette.error.main, label: 'Behind', icon: TrendingDown };
  };

  const statusColor = getStatusColor(performance.hitRate);
  const velocityStatus = getVelocityStatus(performance.velocity);
  const VelocityIcon = velocityStatus.icon;

  // Period labels
  const periodLabels: Record<PerformancePeriod, string> = {
    day: 'Today',
    week: 'This Week',
    month: 'This Month',
    year: 'This Year',
  };

  if (isLoading || isLoadingAll || isLoadingAll) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" height={40} sx={{ mt: 2 }} />
          <Skeleton variant="rounded" height={100} sx={{ mt: 2 }} />
          <Skeleton variant="text" width={100} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Target size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Performance Tracker
            </Typography>
          </Box>
          <Tooltip title="View detailed performance">
            <IconButton size="small" onClick={() => navigate('/tasks')}>
              <ArrowRight size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Period Tabs */}
        <Tabs
          value={period}
          onChange={(_, newValue) => setPeriod(newValue)}
          sx={{
            minHeight: 36,
            mb: 2,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 2,
              fontSize: '0.75rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Day" value="day" />
          <Tab label="Week" value="week" />
          <Tab label="Month" value="month" />
          <Tab label="Year" value="year" />
        </Tabs>

        {/* Hit Rate Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Hit Rate - {periodLabels[period]}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: `${statusColor}.main`,
              }}
            >
              {performance.hitRate}%
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={performance.hitRate}
            color={statusColor}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.100',
              mb: 1,
            }}
          />

          {/* Task Stats */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle size={14} style={{ color: '#22c55e' }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: '#22c55e' }}>{performance.completed}</strong> Done
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <XCircle size={14} style={{ color: '#ef4444' }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: '#ef4444' }}>{performance.missed}</strong> Missed
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BarChart3 size={14} style={{ color: '#6b7280' }} />
              <Typography variant="body2" color="text.secondary">
                <strong>{performance.total}</strong> Total
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Velocity Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Zap size={16} style={{ color: '#f59e0b' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Velocity
              </Typography>
            </Box>
            <Chip
              icon={<VelocityIcon size={12} />}
              label={velocityStatus.label}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: alpha(velocityStatus.color, 0.1),
                color: velocityStatus.color,
                fontWeight: 600,
                '& .MuiChip-icon': { color: velocityStatus.color },
              }}
            />
          </Box>

          {/* Time Stats */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 1.5, 
            p: 1.5, 
            bgcolor: 'grey.50', 
            borderRadius: 2 
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Planned
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={14} style={{ color: '#3b82f6' }} />
                <Typography variant="body2" fontWeight={600}>
                  {performance.estimatedHours.toFixed(1)}h
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Actual
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={14} style={{ color: '#22c55e' }} />
                <Typography variant="body2" fontWeight={600}>
                  {performance.actualHours.toFixed(1)}h
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Velocity Ratio */}
          {performance.velocity > 0 && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Velocity Ratio: 
                <strong style={{ 
                  color: performance.velocity <= 1 ? '#22c55e' : '#f59e0b',
                  marginLeft: 4
                }}>
                  {performance.velocity.toFixed(2)}x
                </strong>
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5 }}>
                {performance.velocity <= 1 
                  ? `${((1 - performance.velocity) * 100).toFixed(0)}% faster than estimated`
                  : `${((performance.velocity - 1) * 100).toFixed(0)}% slower than estimated`
                }
              </Typography>
            </Box>
          )}
        </Box>

        {/* Motivational Message */}
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            px: 1.5,
            py: 1,
            bgcolor: alpha(
              statusColor === 'success' 
                ? theme.palette.success.main
                : statusColor === 'warning' 
                ? theme.palette.warning.main 
                : theme.palette.error.main, 
              0.08
            ),
            borderRadius: 1.5,
            textAlign: 'center',
            borderLeft: 3,
            borderColor: `${statusColor}.main`,
          }}
        >
          <Typography variant="caption" sx={{ color: `${statusColor}.700`, fontWeight: 500, display: 'block' }}>
            {performance.hitRate >= 80
              ? "🎯 Outstanding! Keep up the excellent work!"
              : performance.hitRate >= 60
              ? "💪 Good progress! Push for that 80%!"
              : performance.total === 0
              ? "📝 Start tracking tasks to see your performance"
              : "🚀 Time to accelerate! You've got this!"}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DailyPerformanceWidget;
